import { jsx as _jsx } from "react/jsx-runtime";
// src/plugins/builder/templates/bad-review-email/generator.tsx
// Sends an email-style POST whenever a review with N stars or fewer arrives.
// "Email" here = whatever inbox-side service the user wires up to the webhook
// (Zapier "Email by Zapier", Make.com Mailgun, SendGrid Inbound, etc.). We
// don't ship our own SMTP — the SDK is webhook-first by design.
import meta from './template.json';
import { manifestFor, header, readme, assemble, safeString } from '../_helpers';
import { SimplePage } from '../../runtime/SimplePage';
const RuntimeUi = (ctx) => {
    const cfg = ctx.config;
    return (_jsx(SimplePage, { title: ctx.instanceName, subtitle: `Reviews with ${cfg.stars}★ or fewer get emailed to ${cfg.email}.`, source: "reviews", filterReview: (r) => (r.rating ?? 5) <= (cfg.stars ?? 3), emptyTitle: "No bad reviews yet", emptyBody: "When a low-star review comes in, you'll see it here and get an email." }));
};
const runtimeHooks = {
    async onReviewCreated(ctx, review) {
        const r = review;
        const cfg = ctx.config;
        if (!cfg.email)
            return;
        if ((r.rating ?? 5) > (cfg.stars ?? 3))
            return;
        if (!ctx.permissions.includes('external:fetch'))
            return;
        // POST a tiny JSON payload to the webhook the user pointed at their email gateway.
        await fetch('https://api.reviewradar.app/notify/email', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
                to: cfg.email,
                subject: `New ${r.rating ?? '?'}-star review on ${r.platform}`,
                body: `From: ${r.author_name ?? 'Anonymous'}\n` +
                    `Rating: ${r.rating ?? '?'}\n` +
                    (cfg.includeBody ? `Text: ${r.body ?? ''}\n` : '') +
                    `Open in ReviewRadar: https://app.reviewradar.app/app/reviews\n`,
                source: 'plugin:builder:bad-review-email',
                instanceId: ctx.pluginId,
            }),
        }).catch(() => { });
    },
};
const generate = (params) => {
    const cfg = params.config;
    const manifest = manifestFor({
        instanceId: params.instanceId,
        name: params.instanceName,
        description: `Email ${cfg.email} when a review has ${cfg.stars}★ or fewer.`,
        category: 'automation',
        icon: 'MailWarning',
        permissions: ['read:reviews', 'external:fetch', 'ui:toast'],
        entry: 'both',
        hooks: { onReviewCreated: true },
        config: meta.configSchema,
        defaultConfig: { email: cfg.email, stars: cfg.stars, includeBody: cfg.includeBody },
    });
    const pluginTsx = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import { useReviews } from '../sdk';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';

export default function ${cls(params.instanceId)}() {
  const { reviews } = useReviews({ limit: 50 });
  const bad = (reviews ?? []).filter(r => (r.rating ?? 5) <= ${Number(cfg.stars)});
  if (bad.length === 0) {
    return (
      <main id="main" className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="text-2xl font-semibold">${esc(params.instanceName)}</h1>
        <EmptyState title="No bad reviews yet" body="You'll see them here when they show up." />
      </main>
    );
  }
  return (
    <main id="main" className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold">${esc(params.instanceName)}</h1>
      <p className="text-sm text-slate-500 mt-1">Emailing <strong>${esc(cfg.email)}</strong> when a review is ${Number(cfg.stars)}★ or fewer.</p>
      <ul className="mt-6 space-y-3">
        {bad.map(r => (
          <li key={r.id} className="rounded-2xl bg-white p-4 ring-1 ring-slate-200">
            <p className="text-sm font-medium">{r.author_name ?? 'Anonymous'} · {r.rating}★ · {r.platform}</p>
            <p className="mt-1 text-sm text-slate-600">{r.body}</p>
          </li>
        ))}
      </ul>
      <div className="mt-6"><Button onClick={() => location.reload()}>Refresh</Button></div>
    </main>
  );
}
`;
    const hooksTs = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import type { PluginHandlers, HandlerContext } from '../types';

export const handlers: PluginHandlers = {
  async onReviewCreated(ctx: HandlerContext, review: unknown) {
    const r = review as { rating: number | null; body: string | null; author_name: string | null; platform: string };
    if ((r.rating ?? 5) > ${Number(cfg.stars)}) return;
    if (!ctx.permissions.includes('external:fetch')) return;
    await fetch('https://api.reviewradar.app/notify/email', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        to: '${esc(cfg.email)}',
        subject: \`New \${r.rating ?? '?'}-star review on \${r.platform}\`,
        body: ${cfg.includeBody ? '`From: ${r.author_name ?? "Anonymous"}\\nRating: ${r.rating ?? "?"}\\nText: ${r.body ?? ""}\\n`' : '`A new bad review came in. Open ReviewRadar to read it.`'},
        source: 'plugin:builder:bad-review-email',
      }),
    }).catch(() => {});
  },
};
export default handlers;
`;
    return assemble({
        manifest,
        pluginTsx,
        hooksTs,
        readmeMd: readme({
            name: params.instanceName,
            templateId: meta.id,
            description: `Sends ${cfg.email} a notification whenever a review with ${cfg.stars}★ or fewer arrives.`,
            configSummary: [
                ['email', cfg.email],
                ['stars', String(cfg.stars)],
                ['includeBody', String(cfg.includeBody)],
            ],
        }),
    });
};
function esc(s) { return safeString(String(s)); }
function cls(id) { return 'P_' + id.replace(/[^a-zA-Z0-9]+/g, '_'); }
const template = {
    id: meta.id,
    name: meta.name,
    tagline: meta.tagline,
    icon: meta.icon,
    category: meta.category,
    permissions: meta.permissions,
    permissionSummary: meta.permissionSummary,
    entry: meta.entry,
    hooks: meta.hooks,
    configSchema: meta.configSchema,
    defaultConfig: meta.defaultConfig,
    suggestName: (cfg) => `Bad-Review Alerts to ${cfg.email || 'me'}`,
    describe: (cfg) => {
        const c = cfg;
        return `Send an email to ${c.email || '(your email)'} every time a review has ${c.stars ?? 3}★ or fewer.`;
    },
    generate,
    runtimeUi: RuntimeUi,
    runtimeHooks,
    clickCount: meta.clickCount,
};
export default template;
